import type { MowerId } from '../../content/mowers';
import { loadout, toolLevel, upgradeLevels } from '../economy';
import type { Save } from '../save/schema';
import { RULES_VERSION } from '../rulesVersion';
import type { PostedLawn, PostedRun, RunBoard, RunLoadout } from './run';
import type { SimStatus } from '../../sim/simulation';

/**
 * The game's side of posting runs (docs/ONLINE.md, "The client"): what a finished run sends, and
 * the outbox that holds runs until they are sent. Pure, so the rules are tested here; the network
 * lives in src/platform/online.ts and the save in src/game/online.ts.
 */

/** A run waiting to be sent, and when it was played (ms since 1970). */
export interface PendingRun {
  run: PostedRun;
  at: number;
}

/** Runs kept while offline; past this the oldest go first (a best is likely among the newer). */
export const OUTBOX_LIMIT = 30;

export function addToOutbox(
  outbox: readonly PendingRun[],
  run: PostedRun,
  at: number,
): PendingRun[] {
  return [...outbox, { run, at }].slice(-OUTBOX_LIMIT);
}

/** The mower's upgrades and tools as the game ran them, or undefined when it has none. */
export function loadoutFor(save: Readonly<Save>, mower: MowerId): RunLoadout | undefined {
  const levels = upgradeLevels(save, mower);
  const upgrades = Object.fromEntries(
    Object.entries(levels).filter(
      ([key, n]) => n !== undefined && (n > 0 || key === 'engineInUse'),
    ),
  );
  const tools = loadout(save, mower).map((id): [typeof id, number] => [id, toolLevel(save, id)]);
  const out: RunLoadout = {
    ...(Object.keys(upgrades).length > 0 ? { upgrades } : {}),
    ...(tools.length > 0 ? { tools } : {}),
  };
  return Object.keys(out).length > 0 ? out : undefined;
}

/** A finished run, ready to post. */
export function postedRun(
  board: RunBoard,
  mower: MowerId,
  lawns: PostedLawn[],
  end: { score: number; status: SimStatus },
  runLoadout?: RunLoadout,
): PostedRun {
  return {
    rules: RULES_VERSION,
    board,
    mower,
    ...(runLoadout ? { loadout: runLoadout } : {}),
    lawns,
    claimed: {
      ticks: lawns.reduce((t, l) => t + l.endTick, 0),
      score: end.score,
      status: end.status,
    },
  };
}

/**
 * What to do with a run after trying to send it:
 * - `done`: the server took it (ranked or not);
 * - `drop`: the server will never take it (refused, malformed), so stop trying;
 * - `rejoin`: the player is unknown (deleted elsewhere): get a new one, then try again;
 * - `update`: the game is too old to post: keep it, and stop until the game is updated;
 * - `retry`: offline, rate limited or a server hiccup: keep it for later.
 */
export type SendOutcome = 'done' | 'drop' | 'rejoin' | 'update' | 'retry';

export function sendOutcome(status: number | 'network'): SendOutcome {
  if (status === 'network') return 'retry';
  if (status >= 200 && status < 300) return 'done';
  if (status === 401) return 'rejoin';
  if (status === 426) return 'update';
  if (status === 400 || status === 413 || status === 422) return 'drop';
  return 'retry';
}
